import { API_ENDPOINTS, API_URL } from '@/constants/api';
import type { ApiError } from '@/shared/types/api';
import { handleApiError } from '@/shared/utils/error';
import { formatValidationMessages } from '@/shared/utils/validationMessages';
import axios, { AxiosHeaders } from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipLoader?: boolean;
    _retry?: boolean;
  }

  interface InternalAxiosRequestConfig {
    skipLoader?: boolean;
    _retry?: boolean;
  }
}

interface LoaderContext {
  showLoader: () => void;
  hideLoader: () => void;
}

type ValidationErrors = Record<string, string | string[]>;

type ApiErrorResponseData = {
  code?: string;
  error?: string | ValidationErrors;
  errors?: ValidationErrors;
  message?: string;
};

type RefreshResponse = {
  accessToken: string;
};

let loaderContext: LoaderContext | null = null;

export const setLoaderContext = (context: LoaderContext) => {
  loaderContext = context;
};

export const setAccessToken = (token: string | null) => {
  if (token) {
    localStorage.setItem('accessToken', token);
  } else {
    localStorage.removeItem('accessToken');
  }
};

export const getAccessToken = () => {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let isLoggingOut = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

export const setLoggingOut = (value: boolean) => {
  isLoggingOut = value;
};

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

let inactivityTimeoutCallback: (() => void) | null = null;

export const setInactivityTimeoutCallback = (callback: (() => void) | null) => {
  inactivityTimeoutCallback = callback;
};

api.interceptors.request.use(
  (config) => {
    if (isLoggingOut && config.url !== API_ENDPOINTS.AUTH.LOGOUT) {
      return Promise.reject(new Error('Request cancelled due to logout'));
    }

    // Skip global loader if skipLoader flag is set in config
    if (!config.skipLoader) {
      loaderContext?.showLoader();
    }

    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (inactivityTimeoutCallback && config.url !== API_ENDPOINTS.AUTH.REFRESH) {
      inactivityTimeoutCallback();
    }

    return config;
  },
  (error) => {
    loaderContext?.hideLoader();
    return Promise.reject(handleApiError(error));
  },
);

api.interceptors.response.use(
  (response) => {
    // Only hide loader if it was shown (skipLoader was not set)
    if (!response.config.skipLoader) {
      loaderContext?.hideLoader();
    }
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError<ApiErrorResponseData>(error)) {
      loaderContext?.hideLoader();
      return Promise.reject(handleApiError(error));
    }

    if (!error.config?.skipLoader) {
      loaderContext?.hideLoader();
    }

    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      originalRequest.url !== API_ENDPOINTS.AUTH.REFRESH
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api.request(originalRequest);
          })
          .catch((queueError: unknown) => {
            return Promise.reject(handleApiError(queueError));
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post<RefreshResponse>(API_ENDPOINTS.AUTH.REFRESH);
        const newAccessToken = response.data.accessToken;

        setAccessToken(newAccessToken);
        processQueue(null, newAccessToken);

        originalRequest.headers = AxiosHeaders.from(originalRequest.headers);
        originalRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);
        return api.request(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        setAccessToken(null);

        window.location.reload();

        return Promise.reject(handleApiError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    if (error.code === 'ERR_NETWORK' && error.config && !error.config._retry) {
      error.config._retry = true;

      await new Promise((resolve) => setTimeout(resolve, 100));

      return api.request(error.config);
    }

    if (error.response?.status === 422) {
      const errors = error.response.data.errors ?? error.response.data.error ?? {};
      const errorMessages: string[] = [];

      if (typeof errors === 'object' && !Array.isArray(errors)) {
        Object.values(errors).forEach((messages) => {
          errorMessages.push(...(Array.isArray(messages) ? messages : [messages]));
        });
      } else if (typeof errors === 'string') {
        errorMessages.push(errors);
      }

      const validationError: ApiError = {
        status: 422,
        message:
          errorMessages.length > 0
            ? formatValidationMessages(errorMessages)
            : 'Validation failed. Please check your input.',
        code: error.response.data.code,
      };
      return Promise.reject(handleApiError(validationError));
    }

    const responseError =
      typeof error.response?.data.error === 'string' ? error.response.data.error : undefined;
    const apiError: ApiError = {
      status: error.response?.status || 500,
      message: error.response?.data.message || responseError || error.message,
      code: error.response?.data.code,
    };
    return Promise.reject(handleApiError(apiError));
  },
);

export { authApi } from './auth';
export { api };
